import { embed } from "@/services/embeddings";
import { logger } from "@/services/logger";
import { useSettingsStore } from "@/stores/settingsStore";
import { chunkText } from "./chunking";
import { knowledgeRepository } from "./knowledgeRepository";
import { extractText } from "./textExtraction";

const log = logger.scope("document-processor");

const MAX_CONCURRENT = 2;
const MAX_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 2000;

let active = 0;
const pending: string[] = [];
const listeners = new Set<() => void>();

/** Subscribed by `knowledgeStore` to refresh its document list whenever a
 * processing job changes status, without the processor knowing about
 * Zustand. */
export function onProcessingChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify(): void {
  listeners.forEach((l) => l());
}

export function enqueue(documentId: string): void {
  if (pending.includes(documentId)) return;
  pending.push(documentId);
  pump();
}

function pump(): void {
  while (active < MAX_CONCURRENT && pending.length > 0) {
    const id = pending.shift();
    if (!id) return;
    active++;
    void processDocument(id).finally(() => {
      active--;
      pump();
    });
  }
}

async function processDocument(documentId: string): Promise<void> {
  const document = await knowledgeRepository.getDocument(documentId);
  if (!document) return;

  await knowledgeRepository.updateDocumentStatus(documentId, "processing");
  await knowledgeRepository.setQueueStatus(documentId, "processing");
  notify();

  try {
    const embeddingModel = useSettingsStore.getState().embeddingModelId;
    if (!embeddingModel) {
      throw new Error("No embedding model configured — set one on the Models page.");
    }
    const ollamaHost = useSettingsStore.getState().ollamaHost;

    const text = await extractText(document.sourcePath);
    const chunks = chunkText(text);
    if (chunks.length === 0) {
      throw new Error("No extractable text found in this file.");
    }

    const embedded: { seq: number; content: string; embedding: Float32Array }[] = [];
    for (const chunk of chunks) {
      const embedding = await embed(ollamaHost, embeddingModel, chunk.content);
      embedded.push({ ...chunk, embedding });
    }

    await knowledgeRepository.replaceChunks(documentId, embedded);
    await knowledgeRepository.updateDocumentStatus(documentId, "ready");
    await knowledgeRepository.setQueueStatus(documentId, "done");
    log.info(`Indexed "${document.name}" (${chunks.length} chunks)`);
  } catch (err) {
    const message = err instanceof Error ? err.message : "Processing failed.";
    const attempts = await knowledgeRepository.incrementAttempts(documentId);

    if (attempts < MAX_ATTEMPTS) {
      log.warn(`Processing "${document.name}" failed (attempt ${attempts}/${MAX_ATTEMPTS}); retrying`, err);
      await knowledgeRepository.setQueueStatus(documentId, "queued", message);
      setTimeout(() => enqueue(documentId), RETRY_BASE_DELAY_MS * attempts);
    } else {
      log.error(`Processing "${document.name}" failed after ${MAX_ATTEMPTS} attempts`, err);
      await knowledgeRepository.updateDocumentStatus(documentId, "error", message);
      await knowledgeRepository.setQueueStatus(documentId, "error", message);
    }
  } finally {
    notify();
  }
}

/** Resumes any document left `queued`/`processing` from a prior run that
 * was interrupted (crash, force-quit, drive pulled mid-index) — called once
 * at startup. */
export async function resumeInterrupted(): Promise<void> {
  const ids = await knowledgeRepository.listInterruptedDocumentIds();
  for (const id of ids) enqueue(id);
}
